// ============================================================
// THE TURN'S SHOT - which pair a turn is about on this screen, and the shot
// for it (plan map-display, Track C; the camera rules of 2026-10-07 and the
// review of Track C).
//
//   - A REQUEST is the order moving on, or this viewer's own pick (who acts,
//     whom they aim at). A pick moves the camera only for the GM (who runs
//     every NPC's turn by picking) or for a viewer whose turn it is (or with
//     no order running): a player clicking an enemy during an NPC's turn does
//     not pull the camera off that turn.
//   - OVER THE SHOULDER only when the viewer's own character ACTS NOW: it is
//     their acting-as, and the order is on them (or not running). Never the
//     GM's, who has no character of their own (2026-10-21).
//   - An ATTACK is a beat (`beatPairOf`, 2026-10-21): the newest live one
//     frames its attacker and target - at most once per turn (the hook).
//   - The swing ("never lose the actor") is for two-shots only - over the
//     shoulder is behind the actor on purpose - and never turns the whole
//     move past MAX_SWUNG_TURN.
//
// Pure. Zero React dependency.
// ============================================================
import type { Camera, Viewport } from '../battlefield/projection';
import type { Solid } from '../battlefield/types';
import { tokenHeight } from '../battlefield/tokens';
import { buildScene } from '../battlefield/scene';
import { overShoulder, swungClear, twoShot, type Framed } from './camera';
import { headPoint } from './figures';
import { focusIds, framesTarget } from './focus';

/** Who a shot is about. */
export interface Pair {
  actorId: string | null;
  targetId: string | null;
}

/** This screen's side of a turn: who it acts as and aims at, whose turn the order says, and whether it is the GM's. */
export interface Seat {
  actorId: string | null;
  targetId: string | null;
  turnActorId: string | null;
  isGm: boolean;
}

export type Request = 'order' | 'pick';

/** Does this viewer's own character act now: their acting-as, with the order on them or not running? */
export const actsNow = (seat: Seat) => !!seat.actorId && (!seat.turnActorId || seat.turnActorId === seat.actorId);

function orderPair(seat: Seat): Pair | null {
  if (!seat.turnActorId) return null;
  return { actorId: seat.turnActorId, targetId: seat.turnActorId === seat.actorId ? seat.targetId : null };
}

function pickPair(seat: Seat): Pair | null {
  if (!seat.actorId || !(seat.isGm || actsNow(seat))) return null;
  return { actorId: seat.actorId, targetId: seat.targetId };
}

/**
 * The pair the requests since the last move come to: a pick when it counts,
 * else the order's turn. (Mount, Follow and a new lens ask for both.)
 */
export function pairFor(
  requests: ReadonlySet<Request>,
  seat: Seat,
  placed: (id: string | null) => boolean = () => true,
): Pair | null {
  // A pick of an actor this screen has no marker for holds the camera still - and must not take the turn's move
  // with it (2026-10-21: a GM's pick of an unplaced figure dropped the move to whoever's turn it was).
  const pick = requests.has('pick') ? pickPair(seat) : null;
  return (pick && placed(pick.actorId) ? pick : null) ?? (requests.has('order') ? orderPair(seat) : null);
}

/** An impact as the beat reads it (engine/impacts `Impact`). */
interface BeatImpact {
  id: string;
  combatantId: string;
  attackerId: string | null;
  outcome: string;
  crit: boolean;
  at: number;
}

/** Outcomes that are not a blow at a target: nothing fired, or a figure stepping out of hiding. */
const NO_BEAT = new Set(['jam', 'emerge']);

/**
 * THE ATTACK'S BEAT (2026-10-21: "the camera doesn't move on actions like attack"): the newest attack this screen
 * has not acted on, as the pair it frames - its attacker and its target. Null when there is none to frame: a
 * critical (a held beat, no camera move - the camera rules), a jam or an emergence, a replay (its id carries `~r`),
 * one already seen, or an attacker this screen has no marker for (a hidden one is veiled here). FRESH IS "NOT SEEN
 * BEFORE", never a clock: an attack stamped on another seat's clock must not be judged by this one's.
 */
export function beatPairOf(
  impacts: readonly BeatImpact[],
  o: { seen: ReadonlySet<string>; placed: (id: string | null) => boolean },
): Pair | null {
  const fresh = impacts.filter((i) => !o.seen.has(i.id) && !i.id.includes('~r'));
  if (!fresh.length) return null;
  const newest = fresh.reduce((a, b) => (b.at > a.at ? b : a));
  const frames = !!newest.attackerId && !NO_BEAT.has(newest.outcome) && !newest.crit && o.placed(newest.attackerId);
  return frames ? { actorId: newest.attackerId, targetId: newest.combatantId } : null;
}

type Placed = { x: number; z: number; elevation: number; height?: number; eyeHeight: number };

/** A marker as a shot frames it, or null for one this screen does not have. */
function framedOf(tokens: Readonly<Record<string, Placed>>, id: string | null): Framed | null {
  const t = id ? tokens[id] : undefined;
  return t ? { x: t.x, z: t.z, elevation: t.elevation, height: tokenHeight(t) } : null;
}

/** The pair a shot frames and the others it keeps (the focus); a target past a long shot is left out. */
function framingOf(tokens: Readonly<Record<string, Placed & { id?: string }>>, pair: Pair) {
  const actor = framedOf(tokens, pair.actorId);
  const near = framedOf(tokens, pair.targetId);
  const target = actor && near && framesTarget(actor, near) ? near : null;
  const placed = Object.entries(tokens).map(([id, t]) => ({ id, x: t.x, z: t.z }));
  const inFocus = focusIds(placed, pair.actorId, target ? pair.targetId : null) ?? new Set<string>();
  const others = [...inFocus]
    .filter((id) => id !== pair.actorId && id !== pair.targetId)
    .map((id) => framedOf(tokens, id))
    .filter((f): f is Framed => !!f);
  return { actor, target, others };
}

/** What the shot for a pair needs beyond the pair: the screen, its options and its terrain. */
export interface ShotContext {
  seat: Seat;
  tokens: Readonly<Record<string, Placed>>;
  home: Pick<Camera, 'pitch' | 'fov'>;
  shoulder: boolean;
  xray: boolean;
  solids: readonly Solid[];
  view: Viewport;
}

/** Only terrain this near the action can stand between it and the eye worth swinging round: metres. */
const SWING_REACH_M = 20;

/**
 * The shot for a pair from `camera` (null: hold still - an actor this screen
 * has no marker for). Over the shoulder only when the viewer's own character
 * acts now; otherwise the two-shot from the viewer's own pitch and lens
 * (`home`), which - when the viewer asks never to lose the actor - swings
 * round opaque terrain hiding the actor or the target.
 */
export function shotForPair(pair: Pair, camera: Camera, c: ShotContext): Camera | null {
  const { actor, target, others } = framingOf(c.tokens, pair);
  if (!actor) return null;
  if (target && shotKind(pair, c) === 'OVER THE SHOULDER') return overShoulder(actor, target, c.view);
  const shot = twoShot({ ...camera, ...c.home }, actor, target, c.view, others);
  return c.xray ? swungRound(shot, pair, camera.yaw, c) : shot;
}

/** What a shot is called on the screen's camera label (plan design-gaps, Z6; the session and focus pages). */
export type ShotKind = 'OVER THE SHOULDER' | 'TWO-SHOT' | 'LONG SHOT';

/**
 * The kind of shot `shotForPair` takes for a pair, or null when it holds still: over the shoulder when the viewer's
 * own character acts now with the option on; a long shot when the target is past one (left out of the frame); else
 * the two-shot.
 */
export function shotKind(pair: Pair, c: Pick<ShotContext, 'seat' | 'tokens' | 'shoulder'>): ShotKind | null {
  const { actor, target } = framingOf(c.tokens, pair);
  if (!actor) return null;
  // The viewer's OWN character: the GM has none, and runs every NPC from the two-shot (the Session II ruling).
  const ownActor = !c.seat.isGm && pair.actorId === c.seat.actorId && actsNow(c.seat);
  if (ownActor && c.shoulder && target) return 'OVER THE SHOULDER';
  return !target && pair.targetId && c.tokens[pair.targetId] ? 'LONG SHOT' : 'TWO-SHOT';
}

/** The two-shot swung to see the pair's middles clear, testing only opaque terrain near them. */
function swungRound(shot: Camera, pair: Pair, fromYaw: number, c: ShotContext): Camera {
  const middles = [pair.actorId, pair.targetId].flatMap((id) => (id && c.tokens[id] ? [headPoint(c.tokens[id])] : []));
  const near = c.solids.filter((s) =>
    middles.some((m) => Math.hypot(s.position.x - m.x, s.position.z - m.z) <= SWING_REACH_M),
  );
  if (!near.length) return shot;
  const opaque = (cam: Camera) => buildScene(near, cam, c.view).filter((f) => f.transparency === 0);
  return swungClear(shot, middles, opaque, c.view, fromYaw);
}
